import React, { ReactElement, ReactNode, useState } from "react"
import { useSpringCarousel } from "react-spring-carousel-js"

import Icon from "@components/common/Icon"

interface IProps {
  children: ReactNode
}

const CarouselItem: React.FC<IProps> = ({ children }: IProps) => <div className="carousel__item">
  {children}
</div>

/**
 * This component provides a carousel for pictures, used in the public project profile.
 */
const Carousel: React.FC<IProps> = ({ children }: IProps) => {
  const carouselItems = React.Children.map(children, (child: ReactElement, i) => {
    const id = `carousel-item-${i}`
    if (child)
      return {
        id,
        renderItem: <CarouselItem>{child}</CarouselItem>
      }
  })

  // eslint-disable-next-line @typescript-eslint/unbound-method
  const { carouselFragment, slideToNextItem, slideToPrevItem } = useSpringCarousel({
    items: carouselItems
  })

  const [activeItem, setActiveItem] = useState<number>(1)

  const hasNextItem = () => activeItem < carouselItems.length
  const hasPrevItem = () => activeItem > 1

  const showNextItem = (): void => {
    slideToNextItem()
    setActiveItem(activeItem + 1)
  }

  const showPreviousItem = (): void => {
    slideToPrevItem()
    setActiveItem(activeItem - 1)
  }

  return <div className="carousel">
    {carouselFragment}
    <div className="carousel__controls">
      <div className="d-flex align-items-center">
        <span className="carousel__index">{activeItem}/{carouselItems.length}</span>
        <button className="carousel__arrow" onClick={showPreviousItem} disabled={!hasPrevItem()}>
          <Icon name="arrow-left" size={18} />
        </button>
        <button className="carousel__arrow" onClick={showNextItem} disabled={!hasNextItem()}>
          <Icon name="arrow-right" size={18} />
        </button>
      </div>
    </div>
  </div>
}

export default Carousel
